/* eslint-disable camelcase */
import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useNavigate,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
} from '@stripe/react-stripe-js';
import {
  toast,
} from 'react-toastify';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import config from 'web-config';

import {
  uiActions,
} from 'web-actions';

import {
  getUiState,
} from 'web-selectors';

import {
  Button,
  Spinner,
  AddCardForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import CardsList from './CardsList';
import CouponForm from './CouponForm';
import SubscriptionForm from './SubscriptionForm';
import SubscriptionData from './SubscriptionData';
import ConfirmationModal from './ConfirmationModal';
import PaymentSuccessfulModal from './PaymentSuccessfulModal';

import * as S from './styled';

const stripePromise = loadStripe(config.stripe.key);

const mapState = (state => ({
  plans: coreSelectors.plansSelector(state),
  profile: coreSelectors.getProfile(state),
  subscriptions: coreSelectors.getSubscriptions(state),
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  requestStates: coreSelectors.getPaymentRequestsState(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
  lastFailedInvoice: coreSelectors.getLastFailedInvoice(state),
  namespace: getUiState('namespace')(state),
}));

interface Props {
  required?: boolean,
}

const PaymentsDetails = ({ required = false }: Props) => {
  const navigate = useNavigate();
  const [addCardFormIsShown, setAddCardForm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [paymentSuccessfulModal, setPaymentSuccessfulModal] = useState(false);

  const {
    plans,
    profile,
    namespace,
    subscriptions,
    defaultCardId,
    requestStates,
    paymentMethods,
    lastFailedInvoice,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const isPro = profile.is_pro || required;

  const pastDue = subscriptions[0]?.status === 'past_due';
  const trialing = subscriptions[0]?.status === 'trialing';

  useEffect(() => {
    dispatch(
      coreActions.requestCurrentCustomer(),
    );

    dispatch(
      coreActions.requestSubscriptionPlans({
        namespace: namespace.symbol,
      }),
    );

    dispatch(
      coreActions.requestGetPaymentMethods({
        callbacks: {
          onSuccess: (res: any[]) => {
            if (res.length === 0) {
              setAddCardForm(true);
            }
          },
        },
      }),
    );
  }, []);

  const failedWarn = () => {
    toast.info('Any failed payments will be retried with your new card and app content will be updated shortly afterward.');
  };

  const handleSetPlan = (data: any) => {

    console.log("data", data)

    if (paymentMethods.length) {
      const callbacks = {
        onSuccess: () => {
          if (required) {
            setPaymentSuccessfulModal(true);
          }
          if (trialing) {
            setConfirmationModal(true);
          }
        },
      }

      if (subscriptions[0]) {
        dispatch(
          coreActions.requestUpdateSubscription({
            id: subscriptions[0]?.id,
            body: data.payload,
            callbacks,
          }),
        );

      } else {
        dispatch(
          coreActions.requestSetPlan({
            body: data.payload,
            callbacks,
          }),
        );
      }
    } else {
      dispatch(
        coreActions.setSubscription({
          ...subscriptions[0],
          plan: data?.plan?.data,
          noSaved: data.payload,
        }),
      );
    }
  };

  const handlePaymentAttempt = () => {
    dispatch(
      coreActions.requestPaymentAttempt({
        onSuccess: () => {
          setPaymentSuccessfulModal(true);
        },
      }),
    );
  };

  const handleAddCard = (res, actions) => {
    const afterCardAdd = () => {
      if (subscriptions[0]?.noSaved) {
        dispatch(
          coreActions.requestSetPlan({
            body: subscriptions[0].noSaved,
            callbacks: {
              onSuccess: () => {
                if (required) {
                  setPaymentSuccessfulModal(true);
                }
                if (trialing) {
                  setConfirmationModal(true);
                }
                setAddCardForm(false);
              },
            },
          }),
        );
      } else if (!profile?.id || lastFailedInvoice) {
        dispatch(
          coreActions.requestPaymentAttempt({
            onSuccess: () => {
              setPaymentSuccessfulModal(true);
              setAddCardForm(false);
            },
          }),
        );
      } else {
        setAddCardForm(false);
      }
    };

    if (paymentMethods.length === 0 && pastDue) failedWarn();

    dispatch(
      coreActions.requestCreatePaymentMethod({
        qs: {
          continue_subscription: pastDue,
        },
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: afterCardAdd,
        },
      }),
    );
  };

  const onDeletePaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestDeletePaymentMethod({
        id: method.id,
      }),
    );
  };

  const onSetDefaultPaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    );
  };

  const handleCancelSubscription = (subscription) => {
    const onSuccess = () => {
      if (namespace.symbol === 'strongfirst') {
        dispatch(
          uiActions.confirmModal({
            modal: {
              title: 'Subscription Cancelled',
              bodyText: 'You will be able to access your premium subscription content until the scheduled date of your next payment. At that time, your account will be downgraded to Freemium status and you will be able to access only your free content.',
              hideCancel: true,
              confirmText: 'Ok',
            },
          }),
        );
      }
    };

    const onConfirmAction = coreActions.requestCancelSubscription({
      id: subscription.id,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Cancel Subscription',
          bodyText: 'Are you sure you want to cancel your premium subscription?',
          cancelText: 'No, I changed my mind!',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const handleApplyCoupon = useCallback(({ coupon }) => {
    const subscription = subscriptions.find(s => !s.cancel_at_period_end) || subscriptions[0];

    if (isPro) {
      dispatch(
        coreActions.requestSetPlan({
          body: {
            coupon,
            product_plan_id: subscription?.plan?.id,
          },
          callbacks: {
            onSuccess: (res) => {
              console.log('requestSetPlan response', res);
            },
          },
        }),
      );
    } else {
      dispatch(
        coreActions.requestUpdateSubscription({
          id: subscription?.id,
          body: {
            coupon,
            plan_id: subscription?.plan?.id,
          },
          callbacks: {
            onSuccess: (res) => {
              console.log('requestUpdateSubscription response', res);
            },
          },
        }),
      );
    }
  }, [subscriptions, isPro]);

  return (
    <>
      {(requestStates.getCustomerState === 'pending'
        || requestStates.setPlanState === 'pending'
        || requestStates.paymentAttemptState === 'pending'
      ) && <Spinner over />}

      <Flex
        align="stretch"
        padding="15px"
        width="100%"
        flex="1 1 100%"
        style={{ minHeight: '100%' }}
      >
        <Flex
          column
          flex="0 0 45%"
        >
          <S.SectionCaption>
            Subscriptions
          </S.SectionCaption>
          <S.SectionContainer>
            <SubscriptionData
              isPro={isPro}
              subscriptions={subscriptions}
              paymentAttempt={handlePaymentAttempt}
              lastFailedInvoice={lastFailedInvoice}
              cancelSubscription={handleCancelSubscription}
            />
          </S.SectionContainer>
        </Flex>
        <Flex
          column
          grow={1}
          style={{ minHeight: '100%' }}
        >
          <S.SectionCaption>
            Payment Methods
          </S.SectionCaption>
          <S.SectionContainer
            style={{ borderLeft: 'none' }}
          >
            <Flex
              column
              grow={1}
              width="100%"
              align="center"
              padding="10px"
            >
              {isPro && (
                <SubscriptionForm
                  plan={subscriptions[0]?.plan}
                  plans={plans}
                  setPlan={handleSetPlan}
                />
              )}

              {!isPro && namespace.symbol === 'strongfirst' && (
                <CouponForm
                  onSubmit={handleApplyCoupon}
                />
              )}

              {addCardFormIsShown && (
                <Elements stripe={stripePromise}>
                  <Flex
                    column
                    padding="10px 5%"
                    border="1px solid #c0c0c0"
                    borderRadius="5px"
                    align="center"
                    justify="space-between"
                    margin="30px 15%"
                    width="70%"
                  >
                    <Flex
                      borderBottom="1px solid #808080"
                      width="100%"
                      padding="0 0 10px 0"
                    >
                      <Text
                        size="22px"
                        weight="bold"
                        color="#333"
                      >
                        Credit Card
                      </Text>
                    </Flex>
                    <AddCardForm
                      submitText={(lastFailedInvoice) ? 'Pay' : 'Finish'}
                      onCancel={() => setAddCardForm(false)}
                      onSuccess={handleAddCard}
                    />
                  </Flex>
                </Elements>
              )}
              <CardsList
                defaultCardId={defaultCardId}
                paymentMethods={paymentMethods}
                onDelete={onDeletePaymentMethod}
                onSetDefault={onSetDefaultPaymentMethod}
              />
              {!addCardFormIsShown && (
                <Button
                  appearance="primary"
                  onClick={() => {
                    if (subscriptions[0]?.status === 'past_due') failedWarn();
                    setAddCardForm(true);
                  }}
                  style={{
                    width: '120px',
                    margin: '30px 0 0 0',
                  }}
                >
                  Add new card
                </Button>
              )}
            </Flex>
          </S.SectionContainer>
        </Flex>

        <PaymentSuccessfulModal
          isOpen={paymentSuccessfulModal}
          onRequestClose={() => {
            setPaymentSuccessfulModal(false);
            navigate('/welcome');
          }}
        />

        <ConfirmationModal
          plan={subscriptions[0]?.plan}
          isOpen={confirmationModal}
          periodEnd={subscriptions[0]?.current_period_end}
          onRequestClose={() => {
            setConfirmationModal(false);
          }}
        />
      </Flex>
    </>
  );
};

export default PaymentsDetails;
